<template>
  <section class="dp-layout">
    <!-- 左侧家校通讯录（教学架构：学校-年级-班级） -->
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" />
    </section>

    <!-- 右侧汇总主区域 -->
    <section class="dp-right">
      <div v-if="departmentId > 0" class="all_page_border">
        <!-- 页面顶部标题 -->
        <div class="summary-head">
          <div class="summary-head__title-group">
            <span class="summary-head__tag">校车管理 · 数据汇总</span>
            <h2 class="summary-head__title">{{ departmentName }} · 乘车考勤综合统计</h2>
          </div>
          <div class="summary-head__actions">
            <el-button size="small" type="success" icon="el-icon-download" @click="handleExport">
              导出当前报表 (Excel)
            </el-button>
          </div>
        </div>

        <!-- 顶部核心指标看板 -->
        <div class="overview-grid">
          <div class="overview-card card-students">
            <div class="overview-icon"><i class="el-icon-user"></i></div>
            <div class="overview-info">
              <div class="overview-label">乘车学生总数</div>
              <div class="overview-value">{{ overview.totalStudents }} <span class="unit">人</span></div>
            </div>
          </div>
          <div class="overview-card card-total">
            <div class="overview-icon"><i class="el-icon-s-promotion"></i></div>
            <div class="overview-info">
              <div class="overview-label">离校总人次</div>
              <div class="overview-value">{{ overview.totalDepartures }} <span class="unit">次</span></div>
            </div>
          </div>
          <div class="overview-card card-terminal">
            <div class="overview-icon"><i class="el-icon-truck"></i></div>
            <div class="overview-info">
              <div class="overview-label">实际车载终端刷卡</div>
              <div class="overview-value">{{ overview.terminalDepartures }} <span class="unit">次</span></div>
            </div>
          </div>
          <div class="overview-card card-schedule">
            <div class="overview-icon"><i class="el-icon-date"></i></div>
            <div class="overview-info">
              <div class="overview-label">班主任自动排班</div>
              <div class="overview-value">{{ overview.scheduleDepartures }} <span class="unit">次</span></div>
            </div>
          </div>
        </div>

        <!-- 选项卡与报表表格（参考 HomeVisit/Statistics 结构） -->
        <el-tabs v-model="activeTab" class="summary-stat-tabs" @tab-click="handleTabClick">
          <!-- 1. 学生月度汇总 -->
          <el-tab-pane label="学生月度汇总" name="month">
            <div class="tab-filter-bar">
              <span class="filter-label">统计月份：</span>
              <el-date-picker
                v-model="filterMonth"
                type="month"
                value-format="yyyy-MM"
                placeholder="选择统计月份"
                size="small"
                style="width: 140px;"
                :clearable="false"
                @change="onFilterChange"
              />

              <span class="filter-label" style="margin-left: 12px;">数据来源：</span>
              <el-select v-model="filterSource" placeholder="数据来源" size="small" style="width: 140px;" @change="onFilterChange">
                <el-option label="全部数据来源" value="all" />
                <el-option label="仅实际车载终端" value="terminal" />
                <el-option label="仅班主任自动排班" value="class_schedule" />
              </el-select>

              <el-input
                v-model="searchKeyword"
                placeholder="学生姓名/卡号"
                size="small"
                prefix-icon="el-icon-search"
                clearable
                style="width: 160px; margin-left: 12px;"
                @keyup.enter.native="onFilterChange"
              />

              <el-button size="small" type="primary" icon="el-icon-search" style="margin-left: 10px;" @click="onFilterChange">
                查询
              </el-button>
            </div>

            <div class="tbl-scroll-host">
              <el-table
                ref="monthTable"
                :data="monthList"
                v-loading="loading"
                border
                stripe
                size="small"
                class="custom-tbl"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
                style="width: 100%"
                height="100%"
              >
                <el-table-column label="年级" prop="gradeName" min-width="110" show-overflow-tooltip />
                <el-table-column label="班级" prop="className" min-width="120" show-overflow-tooltip />
                <el-table-column label="学生姓名" prop="studentName" min-width="100">
                  <template slot-scope="{ row }">
                    <span class="student-name">{{ row.studentName }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="卡号/学号" prop="cardNumber" min-width="130" show-overflow-tooltip />
                <el-table-column label="当月坐车总次数" prop="totalCount" min-width="120" align="center">
                  <template slot-scope="{ row }">
                    <span class="count-badge count-total">{{ row.totalCount }} 次</span>
                  </template>
                </el-table-column>
                <el-table-column label="实际终端刷卡" prop="terminalCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <span>{{ row.terminalCount }} 次</span>
                  </template>
                </el-table-column>
                <el-table-column label="班主任自动排班" prop="scheduleCount" min-width="120" align="center">
                  <template slot-scope="{ row }">
                    <span>{{ row.scheduleCount }} 次</span>
                  </template>
                </el-table-column>
                <el-table-column label="最后一次坐车时间" prop="lastTouchTime" min-width="165" show-overflow-tooltip />
                <el-table-column label="操作" width="96" align="center">
                  <template slot-scope="{ row }">
                    <el-button size="mini" type="primary" plain icon="el-icon-document" @click="openDetailDialog(row)">
                      明细
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <el-col :span="24" class="toolbar">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="page"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="totalCount"
                style="float: right"
              />
            </el-col>
          </el-tab-pane>

          <!-- 2. 学生每日明细汇总 -->
          <el-tab-pane label="学生每日明细汇总" name="daily">
            <div class="tab-filter-bar">
              <span class="filter-label">日期范围：</span>
              <el-date-picker
                v-model="filterDateRange"
                type="daterange"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 230px;"
                :clearable="false"
                @change="onFilterChange"
              />

              <span class="filter-label" style="margin-left: 12px;">数据来源：</span>
              <el-select v-model="filterSource" placeholder="数据来源" size="small" style="width: 140px;" @change="onFilterChange">
                <el-option label="全部数据来源" value="all" />
                <el-option label="仅实际车载终端" value="terminal" />
                <el-option label="仅班主任自动排班" value="class_schedule" />
              </el-select>

              <el-input
                v-model="searchKeyword"
                placeholder="学生姓名/卡号"
                size="small"
                prefix-icon="el-icon-search"
                clearable
                style="width: 160px; margin-left: 12px;"
                @keyup.enter.native="onFilterChange"
              />

              <el-button size="small" type="primary" icon="el-icon-search" style="margin-left: 10px;" @click="onFilterChange">
                查询
              </el-button>
            </div>

            <div class="tbl-scroll-host">
              <el-table
                ref="dailyTable"
                :data="dailyList"
                v-loading="loading"
                border
                stripe
                size="small"
                class="custom-tbl"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
                style="width: 100%"
                height="100%"
              >
                <el-table-column label="日期" prop="date" min-width="110" />
                <el-table-column label="星期" prop="weekDayName" width="75" align="center" />
                <el-table-column label="班级" prop="className" min-width="120" show-overflow-tooltip />
                <el-table-column label="学生姓名" prop="studentName" min-width="100">
                  <template slot-scope="{ row }">
                    <span class="student-name">{{ row.studentName }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="卡号/学号" prop="cardNumber" min-width="130" show-overflow-tooltip />
                <el-table-column label="当天乘车次数" prop="totalCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <div class="daily-count-cell">
                      <span class="count-badge count-daily">{{ row.totalCount }} 次</span>
                      <span v-if="row.totalCount > 1" class="multiple-ride-tag">多次</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="实际车载终端" prop="terminalCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <span>{{ row.terminalCount }} 次</span>
                  </template>
                </el-table-column>
                <el-table-column label="自动排班推送" prop="scheduleCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <span>{{ row.scheduleCount }} 次</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="96" align="center">
                  <template slot-scope="{ row }">
                    <el-button size="mini" type="primary" plain icon="el-icon-document" @click="openDailyDetailDialog(row)">
                      流水
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <el-col :span="24" class="toolbar">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="page"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="totalCount"
                style="float: right"
              />
            </el-col>
          </el-tab-pane>

          <!-- 3. 班级每日统计 -->
          <el-tab-pane label="班级每日统计" name="class">
            <div class="tab-filter-bar">
              <span class="filter-label">日期范围：</span>
              <el-date-picker
                v-model="filterDateRange"
                type="daterange"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                size="small"
                style="width: 230px;"
                :clearable="false"
                @change="onFilterChange"
              />

              <span class="filter-label" style="margin-left: 12px;">数据来源：</span>
              <el-select v-model="filterSource" placeholder="数据来源" size="small" style="width: 140px;" @change="onFilterChange">
                <el-option label="全部数据来源" value="all" />
                <el-option label="仅实际车载终端" value="terminal" />
                <el-option label="仅班主任自动排班" value="class_schedule" />
              </el-select>

              <el-button size="small" type="primary" icon="el-icon-search" style="margin-left: 12px;" @click="onFilterChange">
                查询
              </el-button>
            </div>

            <div class="tbl-scroll-host">
              <el-table
                ref="classTable"
                :data="classList"
                v-loading="loading"
                border
                stripe
                size="small"
                class="custom-tbl"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
                style="width: 100%"
                height="100%"
              >
                <el-table-column label="日期" prop="date" min-width="110" />
                <el-table-column label="年级" prop="gradeName" min-width="110" show-overflow-tooltip />
                <el-table-column label="班级" prop="className" min-width="120" show-overflow-tooltip />
                <el-table-column label="乘车去重人数" prop="studentCount" min-width="115" align="center">
                  <template slot-scope="{ row }">
                    <span class="count-badge count-student click-badge" @click="openClassStudentsDialog(row, 'all')">
                      {{ row.studentCount }} 人
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="离校总人次" prop="totalTimes" min-width="105" align="center">
                  <template slot-scope="{ row }">
                    <span class="count-badge count-total click-badge" @click="openClassStudentsDialog(row, 'all')">
                      {{ row.totalTimes }} 次
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="实际终端人次" prop="terminalCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <span class="text-link-badge" @click="openClassStudentsDialog(row, 'terminal')">
                      {{ row.terminalCount }} 次
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="自动排班人次" prop="scheduleCount" min-width="110" align="center">
                  <template slot-scope="{ row }">
                    <span class="text-link-badge" @click="openClassStudentsDialog(row, 'class_schedule')">
                      {{ row.scheduleCount }} 次
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="自动排班计划人数" prop="schedulePlanCount" min-width="135" align="center" />
                <el-table-column label="排班已完成人数" prop="scheduleProcessedCount" min-width="120" align="center" />
                <el-table-column label="操作" width="96" align="center">
                  <template slot-scope="{ row }">
                    <el-button size="mini" type="primary" plain icon="el-icon-document" @click="openClassStudentsDialog(row, 'all')">
                      明细
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <el-col :span="24" class="toolbar">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="page"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="totalCount"
                style="float: right"
              />
            </el-col>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 未选组织提示 -->
      <div v-else class="empty-placeholder">
        <i class="el-icon-data-analysis" style="font-size: 56px; color: #c0c4cc; margin-bottom: 16px;"></i>
        <p>请在左侧组织架构中选择学校或班级查看乘车统计</p>
      </div>
    </section>

    <!-- 乘车流水记录明细弹窗 -->
    <el-dialog :title="detailDialogTitle" :visible.sync="detailDialogVisible" width="680px" append-to-body>
      <el-table :data="detailList" border stripe size="small" v-loading="detailLoading" max-height="400">
        <el-table-column label="打卡/离校时间" prop="touchTime" width="170"></el-table-column>
        <el-table-column label="数据来源" width="140" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.recordSource === 'class_schedule'" size="mini" type="primary">班主任自动排班</el-tag>
            <el-tag v-else size="mini" type="success">实际校车终端</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="考勤设备/终端名称" prop="terminalName" min-width="160"></el-table-column>
        <el-table-column label="进出方向" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" type="warning">{{ scope.row.updownDirection || '离开校园' }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" type="primary" @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 班级每日乘车学生名单弹窗（分页每页8条） -->
    <el-dialog
      :title="classStudentsDialogTitle"
      :visible.sync="classStudentsDialogVisible"
      width="880px"
      custom-class="class-students-dialog"
      append-to-body
    >
      <div class="class-dialog-toolbar">
        <el-radio-group v-model="classStudentsSource" size="small" @change="onClassStudentsSourceChange">
          <el-radio-button label="all">全部来源</el-radio-button>
          <el-radio-button label="terminal">仅实际终端</el-radio-button>
          <el-radio-button label="class_schedule">仅自动排班</el-radio-button>
        </el-radio-group>
        <span class="class-dialog-total-tip">共 <b>{{ classStudentsTotal }}</b> 名乘车学生（每页 8 条）</span>
      </div>

      <el-table :data="classStudentsList" border stripe size="small" v-loading="classStudentsLoading" max-height="360" style="width: 100%;">
        <el-table-column label="学生姓名" prop="studentName" min-width="110">
          <template slot-scope="{ row }">
            <span class="student-name">{{ row.studentName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="卡号/学号" prop="cardNumber" min-width="140" show-overflow-tooltip />
        <el-table-column label="班级" prop="className" min-width="120" show-overflow-tooltip />
        <el-table-column label="当天乘车次数" prop="totalCount" min-width="110" align="center">
          <template slot-scope="{ row }">
            <div class="daily-count-cell">
              <span class="count-badge count-daily">{{ row.totalCount }} 次</span>
              <span v-if="row.totalCount > 1" class="multiple-ride-tag">多次</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="实际车载终端" prop="terminalCount" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.terminalCount }} 次</span>
          </template>
        </el-table-column>
        <el-table-column label="自动排班推送" prop="scheduleCount" min-width="110" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.scheduleCount }} 次</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" type="primary" plain icon="el-icon-document" @click="openDailyDetailDialog(row)">
              流水
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="class-dialog-pager">
        <el-pagination
          layout="total, prev, pager, next, jumper"
          :current-page="classStudentsPage"
          :page-size="classStudentsPageSize"
          :total="classStudentsTotal"
          @current-change="handleClassStudentsPageChange"
        />
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" type="primary" @click="classStudentsDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane';
import {
  getSchoolBusOverview,
  getStudentMonthSummary,
  getStudentDailySummary,
  getClassDailySummary,
  getDepartureDetails,
  exportSchoolBusSummary
} from '@/api/modules/schoolBusSchedule';

export default {
  name: 'SchoolBusSummary',
  components: {
    DepartmentPane
  },
  data() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const curDate = `${y}-${m}-${d}`;
    const firstDate = `${y}-${m}-01`;

    return {
      departmentId: 0,
      departmentName: '',
      activeTab: 'month',
      filterMonth: `${y}-${m}`,
      filterDateRange: [firstDate, curDate],
      filterSource: 'all',
      searchKeyword: '',
      loading: false,
      page: 1,
      pageSize: 10,
      totalCount: 0,
      overview: {
        totalStudents: 0,
        totalDepartures: 0,
        terminalDepartures: 0,
        scheduleDepartures: 0
      },
      monthList: [],
      dailyList: [],
      classList: [],
      detailDialogVisible: false,
      detailDialogTitle: '乘车流水明细',
      detailLoading: false,
      detailList: [],
      classStudentsDialogVisible: false,
      classStudentsDialogTitle: '班级乘车学生名单',
      classStudentsLoading: false,
      classStudentsList: [],
      classStudentsPage: 1,
      classStudentsPageSize: 8,
      classStudentsTotal: 0,
      classStudentsCurrentRow: null,
      classStudentsSource: 'all'
    };
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      this.layoutTable(`${this.activeTab}Table`);
    },
    layoutTable(refName) {
      this.$nextTick(() => {
        const run = () => {
          const tbl = this.$refs[refName];
          if (tbl && typeof tbl.doLayout === 'function') {
            tbl.doLayout();
          }
        };
        run();
        setTimeout(run, 80);
      });
    },
    treeNodeClick(data) {
      const deptId = data ? (data.value !== undefined ? data.value : data.id) : 0;
      if (!deptId) return;
      this.departmentId = deptId;
      this.departmentName = data.label || data.name || '';
      this.page = 1;
      this.loadOverview();
      this.fetchData();
    },
    handleTabClick() {
      this.page = 1;
      this.fetchData();
      this.layoutTable(`${this.activeTab}Table`);
    },
    onFilterChange() {
      this.page = 1;
      this.loadOverview();
      this.fetchData();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.page = 1;
      this.fetchData();
    },
    handleCurrentChange(val) {
      this.page = val;
      this.fetchData();
    },
    async loadOverview() {
      if (!this.departmentId) return;
      try {
        let startDate = '';
        let endDate = '';
        if (this.activeTab === 'month') {
          startDate = `${this.filterMonth}-01`;
          endDate = `${this.filterMonth}-31`;
        } else if (this.filterDateRange && this.filterDateRange.length === 2) {
          startDate = this.filterDateRange[0];
          endDate = this.filterDateRange[1];
        }

        const res = await getSchoolBusOverview({
          schoolDepartmentId: this.departmentId,
          startDate,
          endDate,
          source: this.filterSource
        });
        if (res.data && res.data.success && res.data.response) {
          const resp = res.data.response;
          this.overview = {
            totalStudents: resp.TotalStudents !== undefined ? resp.TotalStudents : (resp.totalStudents || 0),
            totalDepartures: resp.TotalDepartures !== undefined ? resp.TotalDepartures : (resp.totalDepartures || 0),
            terminalDepartures: resp.TerminalDepartures !== undefined ? resp.TerminalDepartures : (resp.terminalDepartures || 0),
            scheduleDepartures: resp.ScheduleDepartures !== undefined ? resp.ScheduleDepartures : (resp.scheduleDepartures || 0)
          };
        }
      } catch (err) {
        console.error('加载概览指标异常', err);
      }
    },
    async fetchData() {
      if (!this.departmentId) return;
      this.loading = true;
      try {
        if (this.activeTab === 'month') {
          const res = await getStudentMonthSummary({
            schoolDepartmentId: this.departmentId,
            month: this.filterMonth,
            keyword: this.searchKeyword,
            source: this.filterSource,
            page: this.page,
            pageSize: this.pageSize
          });
          if (res.data && res.data.success && res.data.response) {
            const list = res.data.response.data || [];
            this.monthList = list.map((item) => ({
              studentId: item.StudentId !== undefined ? item.StudentId : item.studentId,
              studentName: item.StudentName || item.studentName || '',
              cardNumber: item.CardNumber || item.cardNumber || '',
              className: item.ClassName || item.className || '',
              gradeName: item.GradeName || item.gradeName || '',
              totalCount: item.TotalCount !== undefined ? item.TotalCount : (item.totalCount || 0),
              terminalCount: item.TerminalCount !== undefined ? item.TerminalCount : (item.terminalCount || 0),
              scheduleCount: item.ScheduleCount !== undefined ? item.ScheduleCount : (item.scheduleCount || 0),
              lastTouchTime: item.LastTouchTime || item.lastTouchTime || ''
            }));
            this.totalCount = res.data.response.dataCount || 0;
          }
        } else if (this.activeTab === 'daily') {
          const [sDate, eDate] = this.filterDateRange || ['', ''];
          const res = await getStudentDailySummary({
            schoolDepartmentId: this.departmentId,
            startDate: sDate,
            endDate: eDate,
            source: this.filterSource,
            page: this.page,
            pageSize: this.pageSize
          });
          if (res.data && res.data.success && res.data.response) {
            const list = res.data.response.data || [];
            this.dailyList = list.map((item) => ({
              date: item.Date || item.date || '',
              weekDayName: item.WeekDayName || item.weekDayName || '',
              studentId: item.StudentId !== undefined ? item.StudentId : item.studentId,
              studentName: item.StudentName || item.studentName || '',
              cardNumber: item.CardNumber || item.cardNumber || '',
              className: item.ClassName || item.className || '',
              totalCount: item.TotalCount !== undefined ? item.TotalCount : (item.totalCount || 0),
              terminalCount: item.TerminalCount !== undefined ? item.TerminalCount : (item.terminalCount || 0),
              scheduleCount: item.ScheduleCount !== undefined ? item.ScheduleCount : (item.scheduleCount || 0)
            }));
            this.totalCount = res.data.response.dataCount || 0;
          }
        } else if (this.activeTab === 'class') {
          const [sDate, eDate] = this.filterDateRange || ['', ''];
          const res = await getClassDailySummary({
            schoolDepartmentId: this.departmentId,
            startDate: sDate,
            endDate: eDate,
            page: this.page,
            pageSize: this.pageSize
          });
          if (res.data && res.data.success && res.data.response) {
            const list = res.data.response.data || [];
            this.classList = list.map((item) => ({
              date: item.Date || item.date || '',
              gradeName: item.GradeName || item.gradeName || '',
              className: item.ClassName || item.className || '',
              classDepartmentId: item.ClassDepartmentId !== undefined ? item.ClassDepartmentId : (item.classDepartmentId || 0),
              studentCount: item.StudentCount !== undefined ? item.StudentCount : (item.studentCount || 0),
              totalTimes: item.TotalTimes !== undefined ? item.TotalTimes : (item.totalTimes || 0),
              terminalCount: item.TerminalCount !== undefined ? item.TerminalCount : (item.terminalCount || 0),
              scheduleCount: item.ScheduleCount !== undefined ? item.ScheduleCount : (item.scheduleCount || 0),
              schedulePlanCount: item.SchedulePlanCount !== undefined ? item.SchedulePlanCount : (item.schedulePlanCount || 0),
              scheduleProcessedCount: item.ScheduleProcessedCount !== undefined ? item.ScheduleProcessedCount : (item.scheduleProcessedCount || 0)
            }));
            this.totalCount = res.data.response.dataCount || 0;
          }
        }
      } catch (err) {
        this.$message.error('加载统计数据异常：' + (err.message || '网络错误'));
      } finally {
        this.loading = false;
        this.layoutTable(`${this.activeTab}Table`);
      }
    },
    async openDetailDialog(row) {
      this.detailDialogTitle = `学生【${row.studentName}】· ${this.filterMonth} 乘车明细`;
      this.detailDialogVisible = true;
      this.detailLoading = true;
      try {
        const res = await getDepartureDetails({
          studentId: row.studentId,
          startDate: `${this.filterMonth}-01`,
          endDate: `${this.filterMonth}-31`,
          source: this.filterSource
        });
        if (res.data && res.data.success && res.data.response) {
          const list = res.data.response || [];
          this.detailList = list.map((d) => ({
            rawHistoryId: d.RawHistoryId !== undefined ? d.RawHistoryId : d.rawHistoryId,
            touchTime: d.TouchTime || d.touchTime,
            recordSource: d.RecordSource || d.recordSource,
            terminalName: d.TerminalName || d.terminalName,
            updownDirection: d.UpdownDirection || d.updownDirection || '离开校园'
          }));
        } else {
          this.detailList = [];
        }
      } catch (err) {
        this.$message.error('加载学生明细异常');
      } finally {
        this.detailLoading = false;
      }
    },
    async openDailyDetailDialog(row) {
      this.detailDialogTitle = `学生【${row.studentName}】· ${row.date} 乘车明细`;
      this.detailDialogVisible = true;
      this.detailLoading = true;
      try {
        const res = await getDepartureDetails({
          studentId: row.studentId,
          date: row.date,
          source: this.classStudentsDialogVisible ? this.classStudentsSource : this.filterSource
        });
        if (res.data && res.data.success && res.data.response) {
          const list = res.data.response || [];
          this.detailList = list.map((d) => ({
            rawHistoryId: d.RawHistoryId !== undefined ? d.RawHistoryId : d.rawHistoryId,
            touchTime: d.TouchTime || d.touchTime,
            recordSource: d.RecordSource || d.recordSource,
            terminalName: d.TerminalName || d.terminalName,
            updownDirection: d.UpdownDirection || d.updownDirection || '离开校园'
          }));
        } else {
          this.detailList = [];
        }
      } catch (err) {
        this.$message.error('加载流水明细异常');
      } finally {
        this.detailLoading = false;
      }
    },
    openClassStudentsDialog(row, source = 'all') {
      this.classStudentsCurrentRow = row;
      this.classStudentsSource = source || 'all';
      this.classStudentsPage = 1;
      const prefix = row.gradeName ? `${row.gradeName} ` : '';
      this.classStudentsDialogTitle = `【${prefix}${row.className}】· ${row.date} 乘车学生名单`;
      this.classStudentsDialogVisible = true;
      this.fetchClassStudentsList();
    },
    onClassStudentsSourceChange() {
      this.classStudentsPage = 1;
      this.fetchClassStudentsList();
    },
    handleClassStudentsPageChange(val) {
      this.classStudentsPage = val;
      this.fetchClassStudentsList();
    },
    async fetchClassStudentsList() {
      if (!this.classStudentsCurrentRow) return;
      this.classStudentsLoading = true;
      try {
        const res = await getStudentDailySummary({
          schoolDepartmentId: this.departmentId,
          classDepartmentId: this.classStudentsCurrentRow.classDepartmentId || 0,
          startDate: this.classStudentsCurrentRow.date,
          endDate: this.classStudentsCurrentRow.date,
          source: this.classStudentsSource,
          page: this.classStudentsPage,
          pageSize: this.classStudentsPageSize
        });
        if (res.data && res.data.success && res.data.response) {
          const list = res.data.response.data || [];
          this.classStudentsList = list.map((item) => ({
            date: item.Date || item.date || '',
            weekDayName: item.WeekDayName || item.weekDayName || '',
            studentId: item.StudentId !== undefined ? item.StudentId : item.studentId,
            studentName: item.StudentName || item.studentName || '',
            cardNumber: item.CardNumber || item.cardNumber || '',
            className: item.ClassName || item.className || '',
            totalCount: item.TotalCount !== undefined ? item.TotalCount : (item.totalCount || 0),
            terminalCount: item.TerminalCount !== undefined ? item.TerminalCount : (item.terminalCount || 0),
            scheduleCount: item.ScheduleCount !== undefined ? item.ScheduleCount : (item.scheduleCount || 0)
          }));
          this.classStudentsTotal = res.data.response.dataCount || 0;
        } else {
          this.classStudentsList = [];
          this.classStudentsTotal = 0;
        }
      } catch (err) {
        this.$message.error('加载班级学生名单失败：' + (err.message || '网络错误'));
      } finally {
        this.classStudentsLoading = false;
      }
    },
    async handleExport() {
      if (!this.departmentId) {
        this.$message.warning('请先在左侧选择学校或班级');
        return;
      }

      let startDate = '';
      let endDate = '';
      if (this.activeTab === 'month') {
        startDate = `${this.filterMonth}-01`;
        endDate = `${this.filterMonth}-31`;
      } else if (this.filterDateRange && this.filterDateRange.length === 2) {
        startDate = this.filterDateRange[0];
        endDate = this.filterDateRange[1];
      }

      const params = {
        schoolDepartmentId: this.departmentId,
        type: this.activeTab,
        startDate,
        endDate,
        source: this.filterSource
      };
      if (this.activeTab === 'month') {
        params.month = this.filterMonth;
      }

      const loadingMsg = this.$message({
        message: '正在生成 Excel 导出文件，请稍候...',
        type: 'info',
        duration: 0,
        iconClass: 'el-icon-loading'
      });

      try {
        const res = await exportSchoolBusSummary(params);
        loadingMsg.close();
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const tabNameMap = {
          month: '学生月度汇总',
          daily: '学生每日明细',
          class: '班级每日统计'
        };
        link.download = `校车乘车汇总_${tabNameMap[this.activeTab] || this.activeTab}_${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.$message.success('导出成功');
      } catch (err) {
        loadingMsg.close();
        this.$message.error('导出 Excel 失败，请重试');
      }
    }
  }
};
</script>

<style scoped>
.all_page_border {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

/* 顶部标题区 */
.summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 10px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.summary-head__tag {
  display: inline-block;
  font-size: 11px;
  color: #0284c7;
  background: #e0f2fe;
  padding: 1px 8px;
  border-radius: 4px;
  margin-bottom: 2px;
}

.summary-head__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

/* 指标看板 */
.overview-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: 10px 0 8px;
  flex-shrink: 0;
}

.overview-card {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.overview-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-right: 12px;
  flex-shrink: 0;
}

.card-students .overview-icon { background: #eff6ff; color: #2563eb; }
.card-total .overview-icon { background: #f0fdf4; color: #16a34a; }
.card-terminal .overview-icon { background: #fefce8; color: #ca8a04; }
.card-schedule .overview-icon { background: #faf5ff; color: #9333ea; }

.overview-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
}

.overview-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.overview-value .unit {
  font-size: 12px;
  font-weight: normal;
  color: #94a3b8;
  margin-left: 2px;
}

/* 核心：选项卡弹性容器（与 HomeVisit/Statistics 规范一致） */
.summary-stat-tabs {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.summary-stat-tabs >>> .el-tabs__header {
  flex-shrink: 0;
  margin-bottom: 8px;
}

.summary-stat-tabs >>> .el-tabs__content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.summary-stat-tabs >>> .el-tab-pane {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 筛选条 */
.tab-filter-bar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 4px;
}

.filter-label {
  font-size: 13px;
  color: #606266;
}

/* 表格滑动托管区 */
.summary-stat-tabs >>> .el-tab-pane > .tbl-scroll-host {
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  overflow: hidden;
}

.summary-stat-tabs >>> .el-tab-pane > .el-col.toolbar {
  flex: 0 0 auto;
  margin-top: 8px;
}

.student-name {
  font-weight: 500;
  color: #1e293b;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 2px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  border: 1px solid transparent;
  box-sizing: border-box;
  vertical-align: middle;
}

.count-total {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.count-daily {
  background: #fefce8;
  color: #b45309;
  border-color: #fde68a;
}

/* 当天乘车次数单元格并排布局，彻底避免角标覆盖文字与溢出截断。创建于 2026-09-08 */
.daily-count-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  vertical-align: middle;
}

/* 多次乘车专属提示标签（精致平铺，文字与数值清晰独立） */
.multiple-ride-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  white-space: nowrap;
  user-select: none;
  letter-spacing: 0.5px;
}

.count-student {
  background: #f0fdf4;
  color: #15803d;
  border-color: #bbf7d0;
}

.click-badge {
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  user-select: none;
}

.click-badge.count-student:hover {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
}

.click-badge.count-total:hover {
  background: #dbeafe;
  border-color: #93c5fd;
  color: #1e40af;
}

.click-badge.count-daily:hover {
  background: #fef3c7;
  border-color: #fcd34d;
  color: #92400e;
}

.text-link-badge {
  cursor: pointer;
  color: #2563eb;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s;
  user-select: none;
}

.text-link-badge:hover {
  background: #eff6ff;
  text-decoration: underline;
}

.class-dialog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.class-dialog-total-tip {
  font-size: 13px;
  color: #64748b;
}

.class-dialog-total-tip b {
  color: #0f172a;
}

.class-dialog-pager {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

::v-deep .class-students-dialog {
  max-width: 95vw;
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
}

@media screen and (max-width: 768px) {
  .summary-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .summary-head__actions {
    width: 100%;
  }
  .summary-head__actions .el-button {
    width: 100%;
  }
  .overview-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin: 8px 0;
  }
  .overview-card {
    padding: 8px 10px;
  }
  .overview-icon {
    width: 32px;
    height: 32px;
    font-size: 16px;
    margin-right: 8px;
  }
  .overview-value {
    font-size: 16px;
  }
  .tab-filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
}
</style>
